// data/courseSalesContent.js
// All copy for CourseSalesSections.jsx. The page component reads from this file
// section by section instead of holding literal copy in JSX, so the story
// can change without touching layout code.
//
// Narrative order on /courses:
//   hero → marquee → dontNeed → isThisYou → structure → [pricing grid] →
//   refund → [reviews] → finalCta
// Media placeholders carry a label describing the asset to be produced.

export const courseSalesContent = {
  hero: {
    eyebrow: "Video Course Track",
    headline: "The CAD Automation Course",
    sub: "From your first line of C# to enterprise CAD tools. 30 mins a day.",
    cta: "Start Learning",
    socialProof:
      "Built from 8+ years automating SolidWorks, AutoCAD, Revit, and Inventor",
    // Gates the hero media column. Flip to true when the motion graphic is
    // produced; the split layout returns unchanged.
    showMedia: false,
    mediaLabel:
      "Motion graphic: C# code types itself while a SolidWorks part rebuilds in sync",
  },

  marquee: [
    "SolidWorks API",
    "AutoCAD .NET",
    "C# from zero",
    "Real tools, not toy scripts",
    "30 mins a day",
    "Lifetime access",
  ],

  dontNeed: {
    kicker: "01 · Zero prerequisites",
    heading: "You don't need these to start",
    sub: "Most engineers put off automation for years because they think it needs all of this. It doesn't.",
    items: [
      {
        strike: "A computer science degree",
        note: "The course starts from zero. Variables, loops, classes, all taught inside CAD problems, not textbook theory.",
      },
      {
        strike: "Prior coding experience",
        note: "If you can build a parametric model, you already think in logic. We turn that thinking into code.",
      },
      {
        strike: "Expensive software or licenses",
        note: "Visual Studio Community is free. The CAD seat already on your machine is the only other tool.",
      },
      {
        strike: "A career change to software",
        note: "You stay a design engineer. Code becomes one more power tool on your bench.",
      },
      {
        strike: "Hours of free time",
        note: "Lessons are cut for 30 focused minutes a day. Watch one, do the exercise, done.",
      },
    ],
    allYouNeed:
      "All you actually need: a laptop, the CAD seat you already use, and 30 minutes a day.",
  },

  isThisYou: {
    kicker: "02 · A quick check",
    heading: "But first, let us see if this course is for you",
    subheading: "Tell me if this is you:",
    points: [
      "You are a design engineer who loves mechanical design, but also loves to automate it.",
      "You repeat the same modeling, drawing, and BOM work and know it should be a script.",
      "You want to stay in trend as AI reshapes the design industry.",
      "You want to leverage one of the most demanding skills in the design industry.",
      "You have tried macro snippets but cannot build a real tool from scratch.",
      "You do not want to become a software developer. You want to stay an engineer who automates.",
    ],
    footer: "Nodded along to three or more? Keep scrolling. This was built for you.",
    // Gates the checklist's media column, same contract as hero.showMedia.
    showMedia: false,
    mediaLabel:
      "Motion loop: an engineer clicks one button and a full drawing package exports itself",
  },

  structure: {
    kicker: "03 · Inside the course",
    heading: "How the video course is structured",
    sub: "One concept a day. Watch, follow, then build it yourself.",
    cards: [
      {
        pill: "Watch and understand",
        mediaLabel: "SCREEN RECORDING: concept explained with a CAD analogy",
        mediaUrl:
          "https://pub-b8eecc60204945e297a8e070748ebf5b.r2.dev/b73b8bd7-57e9-4c4b-96fe-f1c325f8a3c1.mp4",
        body: "One concept at a time. No topic mixing, no jumping ahead. Watch it, understand it against a CAD problem you already know.",
      },
      {
        pill: "Follow along",
        mediaLabel: "SCREEN RECORDING: split screen, code typed line by line",
        mediaUrl:
          "https://pub-b8eecc60204945e297a8e070748ebf5b.r2.dev/216736a2-6a51-4cab-9d08-ab0eb0e60659.mp4",
        body: "Then a follow along section. You type it with me, line by line, at the same pace.",
      },
      {
        pill: "Code by yourself",
        mediaLabel: "SCREEN RECORDING: exercise page, blank editor, then output",
        mediaUrl:
          "https://pub-b8eecc60204945e297a8e070748ebf5b.r2.dev/aa8ca113-9f1e-4dde-8fba-df12e2976550.mp4",
        body: "Then you code it yourself. An exercise with the inputs, the conditions, and the expected output spelled out.",
      },
    ],
    footer: "30 mins a day. Show up, learn one concept, build it.",
    cta: "See the tracks and pricing",
  },

  refund: {
    kicker: "Our promise",
    heading: "No Risk Refund Policy",
    body: "*We want the investment in this Program to be an absolute no-brainer for you. So, if you watch the video lessons, complete all the classes, and still don’t find the Program useful, drop us an email at mailus@scriptedcad.com and we’ll happily refund your full payment of your purchase.",
    email: "mailus@scriptedcad.com",
    sealTop: "100%",
    sealBottom: "Money back",
  },

  finalCta: {
    heading: "Start your automation journey",
    line: "Lifetime access.",
    cta: "Get Started",
    aboutLink: "Who is behind this course?",
  },
};
